'use client';
import React, { useState, useMemo } from 'react';
import { Activity, Wifi, WifiOff, X, CheckCheck, AlertTriangle, Info, Shield, ArrowLeftRight, TrendingUp, Zap, SlidersHorizontal, ChevronDown, ChevronUp } from 'lucide-react';
import { RealtimeAlert } from '@/hooks/useRealtimeAlerts';

interface RealtimeFeedPanelProps {
  alerts: RealtimeAlert[];
  isConnected: boolean;
  unreadCount: number;
  onMarkAllRead: () => void;
  onDismiss: (id: string) => void;
  onMarkRead: (id: string) => void;
  /** Optional label shown in the panel header */
  label?: string;
  /** Max rows to show before scroll */
  maxVisible?: number;
}

const ALL_SEVERITIES = ['critical', 'warning', 'info'] as const;
const ALL_TYPES = ['portfolio', 'compliance', 'transaction', 'settlement', 'kyc', 'aml', 'system'] as const;
const FREQUENCY_OPTIONS = [
  { value: 'all', label: 'All Events' },
  { value: 'critical_only', label: 'Critical Only' },
  { value: 'critical_warning', label: 'Critical & Warning' },
  { value: 'batched', label: 'Batched (last 5)' },
] as const;

type FrequencyOption = typeof FREQUENCY_OPTIONS[number]['value'];

const typeIcon: Record<string, React.ElementType> = {
  portfolio: TrendingUp,
  compliance: Shield,
  transaction: ArrowLeftRight,
  settlement: ArrowLeftRight,
  kyc: Shield,
  aml: AlertTriangle,
  system: Info,
};

const severityStyles: Record<string, { bar: string; bg: string; text: string; dot: string; badge: string }> = {
  critical: {
    bar: 'border-l-2 border-critical',
    bg: 'bg-critical/5',
    text: 'text-critical',
    dot: 'bg-critical',
    badge: 'bg-critical/10 text-critical border border-critical/20',
  },
  warning: {
    bar: 'border-l-2 border-warning',
    bg: 'bg-warning/5',
    text: 'text-warning',
    dot: 'bg-warning',
    badge: 'bg-warning/10 text-warning border border-warning/20',
  },
  info: {
    bar: 'border-l-2 border-info',
    bg: 'bg-info/5',
    text: 'text-info',
    dot: 'bg-info',
    badge: 'bg-info/10 text-info border border-info/20',
  },
};

const typeLabels: Record<string, string> = {
  portfolio: 'Portfolio',
  compliance: 'Compliance',
  transaction: 'Transaction',
  settlement: 'Settlement',
  kyc: 'KYC',
  aml: 'AML',
  system: 'System',
};

function formatTs(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export default function RealtimeFeedPanel({
  alerts,
  isConnected,
  unreadCount,
  onMarkAllRead,
  onDismiss,
  onMarkRead,
  label = 'Live Event Feed',
  maxVisible = 8,
}: RealtimeFeedPanelProps) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedSeverities, setSelectedSeverities] = useState<Set<string>>(new Set(ALL_SEVERITIES));
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set(ALL_TYPES));
  const [frequency, setFrequency] = useState<FrequencyOption>('all');

  const toggleSeverity = (s: string) => {
    setSelectedSeverities((prev) => {
      const next = new Set(prev);
      if (next.has(s)) { if (next.size > 1) next.delete(s); } else next.add(s);
      return next;
    });
  };

  const toggleType = (t: string) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) { if (next.size > 1) next.delete(t); } else next.add(t);
      return next;
    });
  };

  const filteredAlerts = useMemo(() => {
    let result = alerts.filter(
      (a) => selectedSeverities.has(a.severity) && selectedTypes.has(a.type)
    );
    if (frequency === 'critical_only') {
      result = result.filter((a) => a.severity === 'critical');
    } else if (frequency === 'critical_warning') {
      result = result.filter((a) => a.severity === 'critical' || a.severity === 'warning');
    } else if (frequency === 'batched') {
      result = result.slice(0, 5);
    }
    return result;
  }, [alerts, selectedSeverities, selectedTypes, frequency]);

  const visible = filteredAlerts.slice(0, maxVisible);

  const activeFilterCount =
    (ALL_SEVERITIES.length - selectedSeverities.size) +
    (ALL_TYPES.length - selectedTypes.size) +
    (frequency !== 'all' ? 1 : 0);

  return (
    <div className="rounded-xl border border-border bg-surface-2 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-surface-3/40">
        <div className="flex items-center gap-2.5">
          <Activity size={15} className="text-accent" />
          <span className="text-sm font-semibold text-text-primary">{label}</span>
          {unreadCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-critical text-white text-[10px] font-bold">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Connection indicator */}
          <div className="flex items-center gap-1.5">
            {isConnected ? (
              <>
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-positive opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-positive" />
                </span>
                <Wifi size={12} className="text-positive" />
                <span className="text-[11px] text-positive font-medium">Live</span>
              </>
            ) : (
              <>
                <span className="h-2 w-2 rounded-full bg-text-muted" />
                <WifiOff size={12} className="text-text-muted" />
                <span className="text-[11px] text-text-muted">Connecting…</span>
              </>
            )}
          </div>
          {unreadCount > 0 && (
            <button
              onClick={onMarkAllRead}
              className="flex items-center gap-1 text-[11px] text-text-muted hover:text-text-primary transition-colors"
            >
              <CheckCheck size={12} />
              Mark all read
            </button>
          )}
          {/* Filter toggle */}
          <button
            onClick={() => setFiltersOpen((v) => !v)}
            className={`flex items-center gap-1 text-[11px] transition-colors px-2 py-1 rounded-md border ${
              filtersOpen || activeFilterCount > 0
                ? 'border-accent/40 bg-accent/10 text-accent' :'border-border text-text-muted hover:text-text-primary hover:border-border/80'
            }`}
          >
            <SlidersHorizontal size={11} />
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[14px] h-3.5 px-1 rounded-full bg-accent text-white text-[9px] font-bold">
                {activeFilterCount}
              </span>
            )}
            {filtersOpen ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      {filtersOpen && (
        <div className="px-4 py-3 border-b border-border bg-surface-3/20 space-y-3">
          {/* Severity filter */}
          <div>
            <p className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-1.5">Severity</p>
            <div className="flex flex-wrap gap-1.5">
              {ALL_SEVERITIES.map((s) => {
                const styles = severityStyles[s];
                const active = selectedSeverities.has(s);
                return (
                  <button
                    key={s}
                    onClick={() => toggleSeverity(s)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all ${
                      active ? styles.badge : 'bg-surface-3 text-text-muted border border-border/50 opacity-50'
                    }`}
                  >
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Alert type filter */}
          <div>
            <p className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-1.5">Alert Type</p>
            <div className="flex flex-wrap gap-1.5">
              {ALL_TYPES.map((t) => {
                const active = selectedTypes.has(t);
                return (
                  <button
                    key={t}
                    onClick={() => toggleType(t)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all border ${
                      active
                        ? 'bg-accent/10 text-accent border-accent/30' :'bg-surface-3 text-text-muted border-border/50 opacity-50'
                    }`}
                  >
                    {typeLabels[t]}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Delivery frequency */}
          <div>
            <p className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-1.5">Delivery Frequency</p>
            <div className="flex flex-wrap gap-1.5">
              {FREQUENCY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setFrequency(opt.value)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all border ${
                    frequency === opt.value
                      ? 'bg-accent/10 text-accent border-accent/30' :'bg-surface-3 text-text-muted border-border/50 hover:text-text-primary'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Reset filters */}
          {activeFilterCount > 0 && (
            <button
              onClick={() => {
                setSelectedSeverities(new Set(ALL_SEVERITIES));
                setSelectedTypes(new Set(ALL_TYPES));
                setFrequency('all');
              }}
              className="text-[11px] text-text-muted hover:text-text-primary underline underline-offset-2 transition-colors"
            >
              Reset all filters
            </button>
          )}
        </div>
      )}

      {/* Feed */}
      <div className="divide-y divide-border/50 max-h-72 overflow-y-auto">
        {visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 gap-2 text-text-muted">
            <Zap size={20} className="opacity-40" />
            <p className="text-xs">
              {filteredAlerts.length === 0 && alerts.length > 0
                ? 'No events match current filters' :'Waiting for live events…'}
            </p>
            {filteredAlerts.length === 0 && alerts.length > 0 && (
              <button
                onClick={() => {
                  setSelectedSeverities(new Set(ALL_SEVERITIES));
                  setSelectedTypes(new Set(ALL_TYPES));
                  setFrequency('all');
                }}
                className="text-[11px] text-accent hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          visible.map((alert) => {
            const styles = severityStyles[alert.severity] || severityStyles.info;
            const IconComp = typeIcon[alert.type] || Info;
            return (
              <div
                key={alert.id}
                className={`flex items-start gap-3 px-4 py-3 ${styles.bar} ${styles.bg} ${!alert.read ? 'opacity-100' : 'opacity-60'} transition-opacity cursor-pointer`}
                onClick={() => onMarkRead(alert.id)}
              >
                <div className={`mt-0.5 flex-shrink-0 ${styles.text}`}>
                  <IconComp size={13} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-semibold text-text-primary truncate">{alert.title}</span>
                    {!alert.read && (
                      <span className={`flex-shrink-0 h-1.5 w-1.5 rounded-full ${styles.dot}`} />
                    )}
                  </div>
                  <p className="text-[11px] text-text-muted leading-relaxed line-clamp-2">{alert.message}</p>
                  <span className="text-[10px] text-text-muted/60 mt-0.5 block">{formatTs(alert.ts)}</span>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); onDismiss(alert.id); }}
                  className="flex-shrink-0 mt-0.5 text-text-muted hover:text-text-primary transition-colors"
                >
                  <X size={12} />
                </button>
              </div>
            );
          })
        )}
      </div>

      {filteredAlerts.length > maxVisible && (
        <div className="px-4 py-2 border-t border-border/50 bg-surface-3/20">
          <span className="text-[11px] text-text-muted">+{filteredAlerts.length - maxVisible} more events</span>
        </div>
      )}
    </div>
  );
}
